<template>
  <div class="flex flex-col gap-4 relative">
    <!-- Header -->
    <div class="text-brown-800 font-inter">
      <div class="text-text-display-lg font-black tracking-[-0.03em]">
        Tell us more about yourself!
      </div>
      <div class="text-text-body-md font-semibold mt-[10px]">
        Create your HOGC One account to log in and get started.
      </div>
    </div>

    <!-- Registration Form -->
    <div class="mt-[24px] mb-[32px] flex flex-col gap-6">
      <div>
        <label :class="labelClass" for="fullName">Full name</label>
        <DsInput
          id="fullName"
          name="fullName"
          v-model="form.fullName"
          placeholder="Enter full name"
          :error="shownError('fullName')"
          @blur="touch('fullName')"
        />
      </div>

      <div>
        <label :class="labelClass" for="familyName">Family name</label>
        <DsInput
          id="familyName"
          name="familyName"
          v-model="form.familyName"
          placeholder="Enter family name"
          :error="shownError('familyName')"
          @blur="touch('familyName')"
        />
      </div>

      <div>
        <label :class="labelClass" for="preferredName">Preferred name (optional)</label>
        <DsInput
          id="preferredName"
          name="preferredName"
          v-model="form.preferredName"
          placeholder="Enter preferred name"
          :error="shownError('preferredName')"
          @blur="touch('preferredName')"
        />
      </div>

      <div>
        <span :class="labelClass">Gender</span>
        <div class="flex gap-2">
          <DsRadioCard
            v-for="option in genderCards"
            :key="option.value"
            class="flex-1"
            name="gender"
            align="center"
            hide-indicator
            :model-value="form.gender"
            :value="option.value"
            @update:modelValue="onSelectGender"
          >
            <template #title>
              <span class="inline-flex items-center gap-1">
                {{ option.label }}
                <img :src="option.image" alt="" class="w-[15px]" />
              </span>
            </template>
          </DsRadioCard>
        </div>
      </div>

      <div>
        <label :class="labelClass" for="dateOfBirth">Date of Birth</label>
        <DsDatePicker
          id="dateOfBirth"
          name="dateOfBirth"
          v-model="form.dateOfBirth"
          display-format="DD/MM/YYYY"
          placeholder="Enter Date of Birth"
          :max="today"
          :error="shownError('dateOfBirth')"
          @change="touch('dateOfBirth')"
        />
      </div>

      <div>
        <label :class="labelClass" for="nationality">Nationality</label>
        <DsSearchableDropdown
          id="nationality"
          name="nationality"
          v-model="form.nationality"
          :options="nationalityOptions"
          placeholder="Enter nationality"
          search-placeholder="Search nationality"
          :error="shownError('nationality')"
          @change="touch('nationality')"
        />
      </div>

      <div>
        <span :class="labelClass">Do you have a Connect Group?</span>
        <div class="flex gap-2">
          <DsRadioCard
            v-for="option in cgExistsOptions"
            :key="option"
            class="flex-1"
            name="cgExists"
            align="center"
            hide-indicator
            :model-value="form.cgExists"
            :value="option"
            :label="option"
            @update:modelValue="onSelectCgExists"
          />
        </div>
        <p v-if="shownError('cgExists')" class="mt-1 text-text-body text-status-error">
          {{ shownError('cgExists') }}
        </p>
      </div>

      <div v-if="form.cgExists === 'Yes'">
        <label :class="labelClass" for="cgId">CG</label>
        <DsInput
          id="cgId"
          name="cgId"
          :model-value="form.cgId"
          placeholder="Enter CG"
          :error="shownError('cgId')"
          @update:modelValue="onCgIdInput"
          @blur="touch('cgId')"
        />
      </div>

      <div>
        <label :class="labelClass" for="email">Email</label>
        <DsInput
          id="email"
          name="email"
          type="email"
          v-model="form.email"
          placeholder="example@email.com"
          :error="shownError('email')"
          @blur="touch('email')"
        />
      </div>

      <!-- Submit Button -->
      <div :style="hasFocusedField ? `padding-bottom: ${keyboardInsetCss}` : ''">
        <BottomButton
          text="Next"
          :disabled="isButtonDisabled || isLoading"
          :loading="isLoading"
          @click="handleSubmit"
          class="mt-[24px] w-full"
        />
      </div>
    </div>
  </div>
  <Toast position="top-[48px]" />
</template>
<script>
import {
  DatePicker as DsDatePicker,
  Input as DsInput,
  RadioCard as DsRadioCard,
  SearchableDropdown as DsSearchableDropdown,
} from '@team-hogc/hogc-one-design-system';
import BottomButton from '@/components/common/BottomButton.vue';
import Toast from '../common/Toast.vue';
// Referenced by URL rather than imported: importing them makes webpack emit a second,
// hashed copy of a file that is already copied verbatim into dist/assets.
const maleLogo = '/assets/icons/register/man.webp';
const femaleLogo = '/assets/icons/register/woman.jpg';
import { aboutYourselfErrors, trimFormValues } from '@/utils/registration-validation';
import { mapGetters, mapState } from 'vuex';
import keyboardFieldIntoView from '@/mixins/keyboard-field-into-view';

const EMPTY_FORM = () => ({
  fullName: '',
  familyName: '',
  preferredName: '',
  gender: '',
  dateOfBirth: '',
  nationality: '',
  cgExists: '',
  cgId: '',
  email: '',
});

export default {
  mixins: [keyboardFieldIntoView],
  props: ['cgData', 'registrationPageData'],
  components: {
    BottomButton,
    DsDatePicker,
    DsInput,
    DsRadioCard,
    DsSearchableDropdown,
    Toast,
  },
  data() {
    return {
      form: EMPTY_FORM(),
      // A field's error only shows once the member has been in it (or has
      // pressed Next). The Vueform version hid every message and left the
      // member with a disabled button and no reason for it.
      touched: {},
      labelClass: 'block text-base text-primary font-inter font-medium mb-1 leading-5',
      genderCards: [
        { value: 'male', label: 'Male', image: maleLogo },
        { value: 'female', label: 'Female', image: femaleLogo },
      ],
      cgExistsOptions: ['Yes', 'No'],
    };
  },
  computed: {
    ...mapState('registration', { isLoading: 'loading' }),
    ...mapGetters('registration', ['nationalityForSelect']),
    // The DS dropdown matches option values by identity, and nationality ids
    // arrive as numbers; strings keep the selected option highlighted.
    nationalityOptions() {
      return this.nationalityForSelect.map(({ value, label }) => ({
        value: String(value),
        label,
      }));
    },
    today() {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${now.getFullYear()}-${month}-${day}`;
    },
    errors() {
      return aboutYourselfErrors(this.form, this.cgData);
    },
    isButtonDisabled() {
      return Object.keys(this.errors).length > 0;
    },
  },
  async mounted() {
    this.startKeyboardOverlapTracking();
    await this.$store.dispatch('registration/fetchNationality');
    this.prefillFromStore();
  },
  methods: {
    prefillFromStore() {
      const saved = this.registrationPageData?.aboutyourself ?? {};
      if (Object.keys(saved).length === 0) return;

      Object.assign(this.form, {
        fullName: saved.fullName ?? '',
        familyName: saved.familyName ?? '',
        preferredName: saved.preferredName ?? '',
        gender: saved.gender ?? '',
        dateOfBirth: saved.dateOfBirth ?? '',
        // `nationality` is what this step stores; `nationalityId` is what the
        // register payload uses. Read both so stepping back keeps the choice.
        nationality: String(saved.nationality ?? saved.nationalityId ?? ''),
        email: saved.email ?? '',
      });

      // The answer itself is stored with the rest of the step, so stepping
      // back and forward keeps it; `selectedGroup` and a stored CG are the
      // older ways the same answer reached this form.
      this.form.cgExists =
        saved.cgExists ??
        (this.registrationPageData.selectedGroup === 'Yes' || saved.cgId ? 'Yes' : '');
      if (this.form.cgExists === 'Yes') {
        this.form.cgId = saved.cgId ?? '';
      }
    },
    touch(field) {
      this.touched = { ...this.touched, [field]: true };
    },
    shownError(field) {
      return this.touched[field] ? this.errors[field] || '' : '';
    },
    onSelectGender(value) {
      this.form.gender = value;
      this.touch('gender');
    },
    onSelectCgExists(value) {
      this.form.cgExists = value;
      // Leaving a stale CG behind would send it with the registration.
      if (value !== 'Yes') this.form.cgId = '';
      this.touch('cgExists');
    },
    onCgIdInput(value) {
      this.form.cgId = String(value ?? '').replace(/\s+/g, '');
    },
    async handleSubmit() {
      if (this.isLoading) return;

      const fields = Object.keys(this.form);
      this.touched = Object.fromEntries(fields.map(field => [field, true]));
      if (this.isButtonDisabled) return;

      const requestData = trimFormValues(this.form);
      // The dropdown works in strings; `registration/registerUser` sends this
      // straight on as `nationalityId`, which the API types as a number.
      requestData.nationality = Number(requestData.nationality);
      const previousEmail = this.registrationPageData?.aboutyourself?.email;
      requestData.isEmailUpdated =
        Object.keys(this.registrationPageData.aboutyourself).length > 0
          ? requestData.email !== previousEmail
          : false;

      try {
        await this.$store.dispatch('registration/sendEmailOtp', requestData.email);
        this.$emit('next', requestData);
      } catch (error) {
        console.error('Error during submission:', error);
      }
    },
  },
};
</script>
